import React, { useEffect, useState } from "react";
import { useNavigate, Outlet, Navigate, useLocation } from "react-router-dom";
import SideBar from "../components/admin/SideBar";
import { clearStorage, getStorage, setStorage } from "./localStorage";
import { useLazyCheckAuthQuery } from "../store/apis/auth.api";
import { categoryToSlug, slugToCategory, validCrmSlugs } from "./crmRoutes";
import useIdleLogout from "../hooks/useIdleLogout";

const ProtectedRoute = ({ type = "" }) => {
  // Idle-logout: 60 minutes total, with a 2-minute "still there?" warning
  // before the actual logout. Returns either null (no warning showing) or
  // a modal element that gets rendered alongside the Outlet. Activity is
  // synced across tabs via localStorage so users with the CRM open in
  // multiple tabs only count idle time when they're idle EVERYWHERE.
  // Skipped on /login, /pricing, public-token routes — see hook for list.
  const idleWarning = useIdleLogout({ idleMs: 60 * 60 * 1000, warningMs: 2 * 60 * 1000 });

  const navigate = useNavigate();
  const location = useLocation();
  const [sideBarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  const loginUserToken = getStorage("__login_user_token__");
  const [checkAuth] = useLazyCheckAuthQuery();

  useEffect(() => {
    // If no token at all, redirect to login immediately
    if (!loginUserToken) {
      setIsLoading(false);
      setAuthChecked(true);
      return;
    }

    // Always verify token with backend (CASA Tier 2: no client-side auth bypass)
    const checkIsAuthenticated = async () => {
      try {
        const res = await checkAuth({ type }).unwrap();
        if (!res?.success) {
          console.log("Auth check failed, clearing storage");
          clearStorage();
          setAuthChecked(true);
          setIsLoading(false);
          return;
        }

        // Update user storage with fresh data from server
        if (res?.data?.user) {
          const currentUser = getStorage("__user__", "object") || {};
          const updatedUser = { ...currentUser, ...res.data.user };
          setStorage("__user__", updatedUser, "object");

          // Role-route gate. The /auth/check backend handler ignores
          // the ?type query string, so without these checks a user
          // with a valid token of any role could render the /admin/*
          // or /affiliate/* SPA shell — every gated API call would
          // 403, but the sidebar and route layout would still paint,
          // leaking the names of admin/affiliate tools. Send them to
          // their own dashboard instead.
          if (type === "admin" && res.data.user.role !== "admin") {
            const category = res.data.user?.plan?.category;
            const target = category && categoryToSlug[category]
              ? `/${categoryToSlug[category]}/dashboard`
              : "/realestate/dashboard";
            navigate(target, { replace: true });
            return;
          }
          if (type === "affiliate" && res.data.user.role !== "affiliate") {
            // Admins can preview the affiliate dashboard for QA; everyone
            // else gets bounced to their own context.
            if (res.data.user.role !== "admin") {
              const category = res.data.user?.plan?.category;
              const target = category && categoryToSlug[category]
                ? `/${categoryToSlug[category]}/dashboard`
                : "/realestate/dashboard";
              navigate(target, { replace: true });
              return;
            }
          }

          // Forced-2FA enrollment gate.
          // Backend sets res.data.user.mustSetupTwoFactor when the
          // ENFORCE_AGENT_2FA env var is on AND this user is a non-
          // admin / non-VA created after the cutoff date AND hasn't
          // already enrolled. We let them visit Settings (so they
          // can finish enrollment) but bounce them off anything
          // else. Admins and impersonation sessions bypass — this
          // never fires while debugging.
          if (
            res.data.user.mustSetupTwoFactor &&
            type !== "admin" &&
            !location.pathname.includes("/settings") &&
            !location.pathname.includes("/login")
          ) {
            const category = res.data.user?.plan?.category;
            const slug = (category && categoryToSlug[category]) || "realestate";
            navigate(`/${slug}/settings?tab=security&setupRequired=1`, { replace: true });
            return;
          }

          // Check if user account is disabled (no subscription)
          // Invited seat users bypass this — they inherit their owner's plan
          const isInvitedSeat = res.data.user.isInvitedSeat || !!res.data.user.parentUserId;
          // Suspended (admin-suspended or payment-failed-suspended) AND
          // disabled (subscription-expired) both block access. Without this,
          // suspended users would see the dashboard but get 403s on every
          // paid-feature call — confusing. Redirect to /pricing where they
          // can recover (update payment method or contact support).
          const isBlocked = res.data.user.status === 'disabled' || res.data.user.status === 'suspended';
          if (isBlocked && type !== 'admin' && !isInvitedSeat) {
            navigate("/pricing", { replace: true });
            return;
          }

          // ── Category guard ────────────────────────────────────────────
          // If the user navigates to /wholesale/... but their plan category
          // is real_estate, redirect them to their own CRM. Admins bypass
          // (they have the View-As toggle in Layout). VAs follow their
          // parent's category, which is already populated server-side.
          if (type !== "admin" && !isInvitedSeat) {
            const firstSegment = location.pathname.split("/")[1];
            if (validCrmSlugs.includes(firstSegment)) {
              const isAdmin = res.data.user.role === "admin";
              const userCategory = res.data.user?.plan?.category;
              const expectedSlug = userCategory ? categoryToSlug[userCategory] : null;
              if (!isAdmin && expectedSlug && expectedSlug !== firstSegment) {
                // Preserve the rest of the path so e.g. /wholesale/dashboard
                // becomes /realestate/dashboard for a real-estate user.
                const restOfPath = location.pathname.split("/").slice(2).join("/");
                const target = `/${expectedSlug}${restOfPath ? "/" + restOfPath : "/dashboard"}`;
                navigate(target, { replace: true });
                return;
              }
            }
          }
        }

        setAuthChecked(true);
        setIsLoading(false);
      } catch (error) {
        console.error("Auth error:", error);
        // Don't logout on rate limiting (429) or network errors — only on real auth failures
        const status = error?.status || error?.originalStatus;
        if (status === 429 || status === 'FETCH_ERROR') {
          console.warn("Auth check rate-limited or network error — keeping session");
          setAuthChecked(true);
          setIsLoading(false);
          return;
        }
        clearStorage();
        setAuthChecked(true);
        setIsLoading(false);
      }
    };

    checkIsAuthenticated();
  }, [loginUserToken, checkAuth, type, navigate, location.pathname]);

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--dark-bg)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary-color)] mx-auto mb-4"></div>
          <p className="text-gray-300">Verifying access...</p>
        </div>
      </div>
    );
  }

  // No token = redirect to login
  if (!loginUserToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin layout
  if (type === "admin") {
    return (
      <div className="min-h-screen bg-[var(--lighter-dark)] flex">
        <SideBar
          collapsed={sideBarCollapsed}
          setCollapsed={setSidebarCollapsed}
        />
        <div
          className={`bg-[var(--dark-bg)] flex-1 min-h-screen p-6 ${
            sideBarCollapsed ? "ml-20" : "ml-64"
          } transition-all duration-300`}
        >
          <Outlet />
        </div>
        {idleWarning}
      </div>
    );
  }

  // Regular protected route
  return (
    <>
      <Outlet />
      {idleWarning}
    </>
  );
};

export default ProtectedRoute;
